/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `developer_data_id` message.
 *
 * Must be logged before field description
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class DeveloperDataIdMesg : Mesg {
    public constructor() : super("DeveloperDataId", Profile.MesgNum.DEVELOPER_DATA_ID)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** Number of values stored in developer_id. */
    public val numDeveloperId: Int
        get() = getField(DEVELOPER_ID_FIELD_NUM)?.numValues ?: 0

    /** developer_id. */
    public var developerId: List<UByte?>?
        get() {
            val field = getField(DEVELOPER_ID_FIELD_NUM) ?: return null
            return List(field.numValues) { getDeveloperId(it) }
        }
        set(value) {
            removeField(DEVELOPER_ID_FIELD_NUM)
            value?.forEachIndexed { index, element -> setDeveloperId(index, element) }
        }

    public fun getDeveloperId(index: Int): UByte? =
        getFieldValue(DEVELOPER_ID_FIELD_NUM, index) as? UByte

    public fun setDeveloperId(index: Int, value: UByte?) {
        setFieldValue(DEVELOPER_ID_FIELD_NUM, value, index)
    }

    /** Number of values stored in application_id. */
    public val numApplicationId: Int
        get() = getField(APPLICATION_ID_FIELD_NUM)?.numValues ?: 0

    /** application_id. */
    public var applicationId: List<UByte?>?
        get() {
            val field = getField(APPLICATION_ID_FIELD_NUM) ?: return null
            return List(field.numValues) { getApplicationId(it) }
        }
        set(value) {
            removeField(APPLICATION_ID_FIELD_NUM)
            value?.forEachIndexed { index, element -> setApplicationId(index, element) }
        }

    public fun getApplicationId(index: Int): UByte? =
        getFieldValue(APPLICATION_ID_FIELD_NUM, index) as? UByte

    public fun setApplicationId(index: Int, value: UByte?) {
        setFieldValue(APPLICATION_ID_FIELD_NUM, value, index)
    }

    /** manufacturer_id. */
    public var manufacturerId: Manufacturer?
        get() = (getFieldValue(MANUFACTURER_ID_FIELD_NUM) as? UShort)?.let { Manufacturer.fromValue(it) }
        set(value) = setFieldValue(MANUFACTURER_ID_FIELD_NUM, value?.value)

    /** developer_data_index. */
    public var developerDataIndex: UByte?
        get() = getFieldValue(DEVELOPER_DATA_INDEX_FIELD_NUM) as? UByte
        set(value) = setFieldValue(DEVELOPER_DATA_INDEX_FIELD_NUM, value)

    /** application_version. */
    public var applicationVersion: UInt?
        get() = getFieldValue(APPLICATION_VERSION_FIELD_NUM) as? UInt
        set(value) = setFieldValue(APPLICATION_VERSION_FIELD_NUM, value)

    public companion object {
        public const val DEVELOPER_ID_FIELD_NUM: UByte = 0u
        public const val APPLICATION_ID_FIELD_NUM: UByte = 1u
        public const val MANUFACTURER_ID_FIELD_NUM: UByte = 2u
        public const val DEVELOPER_DATA_INDEX_FIELD_NUM: UByte = 3u
        public const val APPLICATION_VERSION_FIELD_NUM: UByte = 4u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[0] = Field("DeveloperId", 0u, BaseType.BYTE, 1.0, 0.0, "", false)
            fields[1] = Field("ApplicationId", 1u, BaseType.BYTE, 1.0, 0.0, "", false)
            fields[2] = Field("ManufacturerId", 2u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[3] = Field("DeveloperDataIndex", 3u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[4] = Field("ApplicationVersion", 4u, BaseType.UINT32, 1.0, 0.0, "", false)
            return fields
        }
    }
}
